import { API_URL } from '../config';
import axios from 'axios';


const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Normaliza detail do Pydantic v2 (array de objetos) para string
function normalizarDetail(detail) {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg || JSON.stringify(e)).join('; ');
  }
  return String(detail);
}

// Interceptor para tratar erros de autenticacao
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Se ja estamos no login, deixa o erro propagar para a tela de login mostrar
      // "credenciais invalidas". Caso contrario, redireciona e SUSPENDE a Promise
      // pra os componentes nao mostrarem toast de erro durante o redirect.
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
        return new Promise(() => {}); // never resolves; page is leaving
      }
    }
    if (error.response?.data?.detail) {
      error.response.data.detail = normalizarDetail(error.response.data.detail);
    }
    return Promise.reject(error);
  }
);

// Auth API
// Sistemas plugados (ADR 0016)
export const plugAPI = {
  listar: () => api.get('/plug/sistemas'),
  lerManifesto: (url) => api.post('/plug/sistemas/manifesto', { url }),
  cadastrar: (url, abrir_em, url_publica) => api.post('/plug/sistemas', { url, abrir_em, url_publica: url_publica || null }),
  alterar: (codigo, dados) => api.patch(`/plug/sistemas/${codigo}`, dados),
  relerManifesto: (codigo) => api.post(`/plug/sistemas/${codigo}/manifesto`),
  novoSegredo: (codigo) => api.post(`/plug/sistemas/${codigo}/segredo`),
  ticket: (codigo) => api.post(`/plug/${codigo}/ticket`),
};

export const authAPI = {
  login: (email, password) => {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);
    return api.post('/auth/login', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },
  loginGoogle: (credential) => api.post('/auth/google', { credential }),
  recuperarSenha: (email) => api.post('/auth/recuperar-senha', { email }),
  redefinirSenha: (token, novaSenha) =>
    api.post('/auth/redefinir-senha', { token, nova_senha: novaSenha }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  register: (data) => api.post('/auth/registro', data),
  // senhas no corpo, nunca na URL (a URL fica no log de acesso do servidor)
  changePassword: (senhaAtual, novaSenha) =>
    api.put('/auth/alterar-senha', { senha_atual: senhaAtual, nova_senha: novaSenha }),
  listUsers: () => api.get('/auth/usuarios'),
  listAllUsers: () => api.get('/auth/usuarios'), // Para seleção de usuários
  updateUser: (id, data) => api.put(`/auth/usuarios/${id}`, data),
  deleteUser: (id) => api.delete(`/auth/usuarios/${id}`),
  resetPassword: (id, novaSenha) =>
    api.put(`/auth/usuarios/${id}/resetar-senha`, { nova_senha: novaSenha || null }),
  sessoesAtivas: () => api.get('/auth/sessoes-ativas'),
  sessoesHistorico: (params) => api.get('/auth/sessoes-historico', { params }),
  getModulos: () => api.get('/auth/me/modulos'),
  getModulosAcesso: (usuarioId) => api.get(`/auth/usuarios/${usuarioId}/modulos-acesso`),
  // catálogo pelo PERFIL, para decidir o acesso já na criação do usuário
  getModulosAcessoPerfil: (perfil) => api.get(`/auth/perfis/${perfil}/modulos-acesso`),
  // nomes da base de beneficiários, para padronizar o cadastro (só ao criar)
  sugestoesNome: (q) => api.get('/auth/usuarios/sugestoes', { params: { q } }),
  // Papéis por módulo (ADR 0013): {modulo: nivel|null}; null ou o nível base = sem linha
  setPapeisUsuario: (usuarioId, niveis) => api.put(`/auth/usuarios/${usuarioId}/papeis`, { niveis }),
  setModulosOverride: (usuarioId, payload) =>
    api.put(`/auth/usuarios/${usuarioId}/modulos-override`, payload),
  auditoriaModulos: () => api.get('/auth/modulos/auditoria'),
  usuariosDoModulo: (codigo) => api.get(`/auth/modulos/${codigo}/usuarios`),
  // Cadeado de módulos
  getCadeadoConfig: () => api.get('/auth/modulos/cadeado/config'),
  setCadeadoInterruptor: (ativo) => api.post('/auth/modulos/cadeado/interruptor', { ativo }),
  liberarModulo: (codigo) => api.post(`/auth/modulos/${codigo}/liberar`),
  bloquearModulo: (codigo) => api.post(`/auth/modulos/${codigo}/bloquear`),
  solicitarLiberacaoModulo: (codigo) => api.post(`/auth/modulos/${codigo}/solicitar-liberacao`),
};

// Perfis de acesso (ADR 0013, adendo 1): o que um perfil carrega, visível e com histórico
export const perfisAcessoAPI = {
  listar: () => api.get('/auth/perfis-acesso'),
  obter: (id) => api.get(`/auth/perfis-acesso/${id}`),
  criar: (dados) => api.post('/auth/perfis-acesso', dados),
  copiar: (id, dados) => api.post(`/auth/perfis-acesso/${id}/copiar`, dados),
  editar: (id, dados) => api.put(`/auth/perfis-acesso/${id}`, dados),
  regrasFixas: () => api.get('/auth/perfis-acesso/regras-fixas'),
};

// Beneficiarios API
export const beneficiariosAPI = {
  // Marcador de alertas do ERP na busca (uma chamada p/ todos os resultados)
  alertasLote: (matriculas) =>
    api.get('/beneficiarios/alertas-lote', { params: { matriculas: (matriculas || []).join(',') } }),
  buscar: (q, limit = 10) => api.get('/beneficiarios/buscar', { params: { q, limit } }),
  search: (q, limit = 10) => api.get('/beneficiarios/buscar', { params: { q, limit } }), // alias
  get: (id) => api.get(`/beneficiarios/${id}`),
  criar: (data) => api.post('/beneficiarios/', data),
  create: (data) => api.post('/beneficiarios/', data), // alias
  atualizar: (id, data) => api.put(`/beneficiarios/${id}`, data),
  update: (id, data) => api.put(`/beneficiarios/${id}`, data), // alias
  listar: (skip = 0, limit = 20, status = null) =>
    api.get('/beneficiarios/', { params: { skip, limit, status } }),
  list: (skip = 0, limit = 20, status = null) =>
    api.get('/beneficiarios/', { params: { skip, limit, status } }), // alias
  getHistorico: (id) => api.get(`/beneficiarios/${id}/historico`),
  getCarencias: (id) => api.get(`/beneficiarios/${id}/carencias`),
  contagem: () => api.get('/beneficiarios/contagem'),
  statusSync: () => api.get('/beneficiarios/status-sync'),
  sincronizar: () => api.post('/beneficiarios/sincronizar'),
  sincronizarEsau: () => api.post('/beneficiarios/sincronizar-esau'),
  getDashboard: (cpf) => api.get(`/beneficiarios/cpf/${cpf}/dashboard`),
  getUtilizacao: (id, meses = 3) => api.get(`/beneficiarios/${id}/utilizacao`, { params: { meses } }),
  // Cartoes de saude (cache stale-while-revalidate no backend). refresh=true
  // forca ida a operadora (2a via / troca de plano).
  getCartoes: (cpf, refresh = false) =>
    api.get('/beneficiarios/cartoes', { params: { cpf, refresh } }),

  // Quais imagens de cartao o beneficiario tem (funcional / unimed / nenhuma).
  getCartaoImagensDisponiveis: (cpf) =>
    api.get('/beneficiarios/cartoes/imagens', { params: { cpf } }),

  // Imagem (PNG) de UM tipo. Vem como blob porque a rota exige JWT — nao da
  // pra usar <img src> direto. 204 = beneficiario nao tem esse tipo.
  getCartaoImagem: (cpf, tipo = 'funcional') =>
    api.get('/beneficiarios/cartoes/imagem', { params: { cpf, tipo }, responseType: 'blob' }),
};

// Setores API
export const setoresAPI = {
  list: (ativosApenas = true) => api.get('/setores/', { params: { ativos_apenas: ativosApenas } }),
  get: (id) => api.get(`/setores/${id}`),
  create: (data) => api.post('/setores/', data),
  update: (id, data) => api.put(`/setores/${id}`, null, { params: data }),
  delete: (id) => api.delete(`/setores/${id}`),
  getColunas: (setorId) => api.get(`/setores/${setorId}/colunas`),
  createColuna: (setorId, data) => api.post(`/setores/${setorId}/colunas`, data),
  updateColuna: (colunaId, data) => api.put(`/setores/colunas/${colunaId}`, null, { params: data }),
  deleteColuna: (colunaId) => api.delete(`/setores/colunas/${colunaId}`),
  reordenarColunas: (setorId, colunaIds) => api.put(`/setores/${setorId}/colunas/reordenar`, colunaIds),
};

// Cardápio de tarefas (spec tarefas-e-malha)
export const tiposTarefaAPI = {
  list: (params = {}) => api.get('/tipos-tarefa/', { params }),
  create: (data) => api.post('/tipos-tarefa/', data),
  update: (id, data) => api.put(`/tipos-tarefa/${id}`, data),
  desativar: (id) => api.delete(`/tipos-tarefa/${id}`),
};

// Atendimentos API
export const atendimentosAPI = {
  create: (data) => api.post('/atendimentos/', data),
  get: (id) => api.get(`/atendimentos/${id}`),
  buscarProtocolo: (protocolo) => api.get(`/atendimentos/buscar-protocolo/${protocolo}`),
  getKanban: (setorId, filtros = {}) => {
    const params = {};
    if (filtros.protocolo) params.filtro_protocolo = filtros.protocolo;
    if (filtros.nome) params.filtro_nome = filtros.nome;
    if (filtros.cpf) params.filtro_cpf = filtros.cpf;
    if (filtros.status) params.filtro_status = filtros.status;
    return api.get(`/atendimentos/kanban/${setorId}`, { params });
  },
  mover: (id, colunaDestinoId) =>
    api.put(`/atendimentos/${id}/mover`, null, { params: { coluna_destino_id: colunaDestinoId } }),
  transferir: (id, data) => api.post(`/atendimentos/${id}/transferir`, data),
  // resultado: só tarefa (opção do tipo do cardápio) — volta para a fonte que abriu
  finalizar: (id, motivo, resultado) => api.post(`/atendimentos/${id}/finalizar`, resultado ? { motivo, resultado } : { motivo }),
  addAnotacao: (id, data) => api.post(`/atendimentos/${id}/anotacoes`, { ...data, atendimento_id: id }),
  mencionaveis: () => api.get('/atendimentos/mencionaveis'),
  gerenciarCadeado: (id, cadeado, usuariosAutorizados = null) =>
    api.put(`/atendimentos/${id}/cadeado`, null, {
      params: { cadeado, usuarios_autorizados_ids: usuariosAutorizados },
    }),
};

// Documentos API
export const documentosAPI = {
  // Tipos de documento
  listarTipos: (categoria = null, ativos = true) =>
    api.get('/documentos/tipos', { params: { categoria, ativos_apenas: ativos } }),
  criarTipo: (data) => api.post('/documentos/tipos', data),
  atualizarTipo: (id, data) => api.put(`/documentos/tipos/${id}`, data),
  deletarTipo: (id) => api.delete(`/documentos/tipos/${id}`),

  // Documentos de atendimento
  listarPorAtendimento: (atendimentoId) =>
    api.get(`/documentos/atendimento/${atendimentoId}`),
  upload: (atendimentoId, file, tipoDocumentoId = null, descricao = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (tipoDocumentoId) formData.append('tipo_documento_id', tipoDocumentoId);
    if (descricao) formData.append('descricao', descricao);
    return api.post(`/documentos/atendimento/${atendimentoId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletar: (atendimentoId, documentoId) =>
    api.delete(`/documentos/atendimento/${atendimentoId}/${documentoId}`),
  download: (documentoId) =>
    api.get(`/documentos/download/${documentoId}`, { responseType: 'blob' }),
  getDownloadUrl: (documentoId) =>
    `${API_URL}/documentos/download/${documentoId}`,

  // Manuais
  listarManuais: () => api.get('/documentos/manuais'),
  uploadManual: (file, tipoDocumentoId = null, descricao = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (tipoDocumentoId) formData.append('tipo_documento_id', tipoDocumentoId);
    if (descricao) formData.append('descricao', descricao);
    return api.post('/documentos/manuais', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletarManual: (documentoId) => api.delete(`/documentos/manuais/${documentoId}`),

  // Estatisticas
  estatisticas: (dias = 30) =>
    api.get('/documentos/estatisticas', { params: { dias } }),
};

// WhatsApp API
export const whatsappAPI = {
  listarConversas: (status = null, atendimentoId = null, beneficiarioId = null) => {
    const params = {};
    if (status) params.status = status;
    if (atendimentoId) params.atendimento_id = atendimentoId;
    if (beneficiarioId) params.beneficiario_id = beneficiarioId;
    return api.get('/whatsapp/conversas', { params });
  },
  getConversa: (id) => api.get(`/whatsapp/conversas/${id}`),
  listarMensagens: (conversaId) => api.get(`/whatsapp/conversas/${conversaId}/mensagens`),
  enviarMensagem: (conversaId, conteudo) => api.post(`/whatsapp/conversas/${conversaId}/mensagens`, { conteudo }),
  vincular: (conversaId, data) => api.post(`/whatsapp/conversas/${conversaId}/vincular`, data),
  finalizar: (conversaId) => api.post(`/whatsapp/conversas/${conversaId}/finalizar`),
  reabrir: (conversaId) => api.post(`/whatsapp/conversas/${conversaId}/reabrir`),
  mensagensPorAtendimento: (atendimentoId) => api.get(`/whatsapp/atendimento/${atendimentoId}/mensagens`),
  iniciarConversa: (params) => api.post('/whatsapp/conversas/iniciar', null, { params }),
};

// Emails API
export const emailsAPI = {
  estatisticas: (dias = 30) => api.get('/emails/estatisticas', { params: { dias } }),
  logs: (dias = 30, tipo = null, status = null, destinatario = null, skip = 0, limit = 50) => {
    const params = { dias, skip, limit };
    if (tipo) params.tipo = tipo;
    if (status) params.status = status;
    if (destinatario) params.destinatario = destinatario;
    return api.get('/emails/logs', { params });
  },
  getLog: (id) => api.get(`/emails/logs/${id}`),
};

// Inadimplência API
export const inadimplenciaAPI = {
  checkBeneficiario: (matricula) =>
    api.get('/inadimplencia/check-beneficiario', { params: { matricula } }),
  analise: (limite = 50, filtro = '') => api.get('/inadimplencia/analise', { params: { limite, filtro } }),
  snapshotDiario: () => api.post('/inadimplencia/snapshot-diario'),
  boletosRf: (mat) => api.get(`/inadimplencia/rf/${mat}/boletos`),
  // Alerta na busca: uma chamada para todos os resultados (em vez de N)
  checkLote: (matriculas) =>
    api.get('/inadimplencia/check-lote', { params: { matriculas: (matriculas || []).join(',') } }),
  analiseIa: (rf, boletos) => api.post('/inadimplencia/analise/ia', { rf, boletos }),
};

// Dashboard API
export const dashboardAPI = {
  atendimentos: (dias = 30, setorId = null) => {
    const params = { dias };
    if (setorId) params.setor_id = setorId;
    return api.get('/dashboard/atendimentos', { params });
  },
};

export const radarAPI = {
  lista: (params) => api.get('/radar-carteira/lista', { params }),
  scores: (params) => api.get('/radar-carteira/scores', { params }),
  anomalias: (params) => api.get('/radar-carteira/anomalias', { params }),
  statusJob: () => api.get('/radar-carteira/status-job'),
  statusUtilizacao: () => api.get('/radar-carteira/status-utilizacao'),
  executarJob: () => api.post('/radar-carteira/executar-job'),
  sincronizarUtilizacao: (full = false) => api.post(`/radar-carteira/sincronizar-utilizacao?full=${full}`),
  resolverAnomalia: (id) => api.put(`/radar-carteira/anomalias/${id}/resolver`),
  analiseIa: (id, meses = 12) => api.get(`/radar-carteira/beneficiario/${id}/analise-ia`, { params: { meses } }),
  saudeCorporativa: (params) => api.get('/radar-carteira/saude-corporativa', { params }),
  saudeCorporativaDetalhe: (params) => api.get('/radar-carteira/saude-corporativa/detalhe', { params }),
};

// Malha (Fraudes): réguas + Ação + motor (spec tarefas-e-malha)
export const malhaAPI = {
  medidas: () => api.get('/malha/medidas'),
  listar: () => api.get('/malha/reguas'),
  criar: (dados) => api.post('/malha/reguas', dados),
  atualizar: (id, dados) => api.put(`/malha/reguas/${id}`, dados),
  excluir: (id) => api.delete(`/malha/reguas/${id}`),
  historico: (id) => api.get(`/malha/reguas/${id}/historico`),
  simular: (medida, limite, meses = 12) => api.post('/malha/simular', null, { params: { medida, limite, meses } }),
  rodar: ({ mes, regua_id } = {}) => api.post('/malha/rodar', null, { params: { mes: mes || undefined, regua_id: regua_id || undefined }, timeout: 10 * 60 * 1000 }),
  resumo: (mes) => api.get('/malha/resumo', { params: { mes: mes || undefined } }),
  proposta: (regua_id, matricula) => api.get('/malha/balizamentos/proposta', { params: { regua_id, matricula } }),
  aprovadores: () => api.get('/malha/aprovadores'),
  propostaParecer: (cardId) => api.get(`/malha/parecer/${cardId}/proposta`),
  registrarParecer: (cardId, dados) => api.post(`/malha/parecer/${cardId}`, dados),
  subtarefas: (cardId) => api.get(`/malha/parecer/${cardId}/subtarefas`),
  decidir: (subId, aprovar, comentario) => api.post(`/malha/subtarefas/${subId}/decidir`, { aprovar, comentario: comentario || null }),
  balizar: (dados) => api.post('/malha/balizamentos', dados),
  revogar: (id, motivo) => api.post(`/malha/balizamentos/${id}/revogar`, { motivo: motivo || null }),
};

export const emailTemplatesAPI = {
  listar: () => api.get('/email-templates/'),
  obter: (id) => api.get(`/email-templates/${id}`),
  criar: (data) => api.post('/email-templates/', data),
  atualizar: (id, data) => api.put(`/email-templates/${id}`, data),
  deletar: (id) => api.delete(`/email-templates/${id}`),
  variaveis: () => api.get('/email-templates/variaveis'),
  categorias: () => api.get('/email-templates/categorias'),
  uploadImagem: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/email-templates/upload-imagem', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importarDocumento: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/email-templates/importar-documento', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  preview: (id, matRespFin) => api.post(`/email-templates/${id}/preview?mat_resp_fin=${matRespFin}`),
  enviar: (id, matRespFins) => api.post(`/email-templates/${id}/enviar`, { mat_resp_fins: matRespFins }),
};

export default api;
